import { Link } from 'react-router-dom'
import { Server } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'

// 特定商取引法に基づく表記。文面の根拠と確認事項は docs/22-commerce-disclosure-draft.md。
// 日本の法律に基づく表記なので日本語のみ(英語版を作るかは住所の手続き後に決める)。
//
// 販売事業者の氏名・所在地・電話番号は決まりしだい埋める。null の間は「準備中」と表示する。
// 販売事業者は屋号だけでは不可。氏名は「請求があれば遅滞なく開示」と書いて省略している(docs/22 の案B)。
// 開示の請求(support@ 宛てのメール)には、すみやかにメールで氏名を返す。
const SELLER_NAME: string | null = 'RMS(氏名: 請求があった場合は遅滞なく開示します)'
const SELLER_ADDRESS: string | null = '〒160-0022 東京都新宿区新宿1-36-2 新宿第七葉山ビル 3F'
const SELLER_PHONE: string | null = '080-6482-4607'

// Pro の月額(米ドル)。サーバーの Checkout の文言(apps/server/src/domain/billing/checkout.ts)と料金表示もあわせて直す
const PRO_MONTHLY_PRICE_USD = 9

const PREPARING = '準備中です。お問い合わせはメールでお願いします。'

function PublicHeader() {
  const { t, i18n } = useTranslation()
  const toggleLang = () => {
    const next = i18n.language === 'ja' ? 'en' : 'ja'
    void i18n.changeLanguage(next)
    localStorage.setItem('i18n-lang', next)
  }
  return (
    <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-sm">
      <div className="mx-auto max-w-5xl flex h-14 items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-2 text-sm font-semibold">
          <Server className="h-4 w-4 text-primary" />
          ScenarioMock
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={toggleLang} className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2">
            {t('legal.switchLang')}
          </button>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/login">{t('legal.login')}</Link>
          </Button>
          <Button size="sm" asChild>
            <Link to="/signup">{t('legal.startFree')}</Link>
          </Button>
        </div>
      </div>
    </header>
  )
}

function PublicFooter() {
  const { t } = useTranslation()
  return (
    <footer className="border-t py-8 px-6 mt-16">
      <div className="mx-auto max-w-5xl flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Server className="h-4 w-4 text-primary" />
          ScenarioMock
        </div>
        <nav className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap justify-center">
          <Link to="/" className="hover:text-foreground transition-colors">{t('legal.top')}</Link>
          <Link to="/terms" className="hover:text-foreground transition-colors">{t('legal.terms')}</Link>
          <Link to="/privacy" className="hover:text-foreground transition-colors">{t('legal.privacy')}</Link>
          <Link to="/legal/commerce" className="hover:text-foreground transition-colors">{t('legal.commerce')}</Link>
        </nav>
        <p className="text-xs text-muted-foreground">© 2026 ScenarioMock</p>
      </div>
    </footer>
  )
}

const yearly = PRO_MONTHLY_PRICE_USD * 12

const ROWS: Array<{ label: string; value: React.ReactNode }> = [
  { label: '販売事業者', value: SELLER_NAME ?? PREPARING },
  { label: '所在地', value: SELLER_ADDRESS ?? PREPARING },
  { label: '電話番号', value: SELLER_PHONE ?? PREPARING },
  {
    label: 'メールアドレス',
    value: (
      <a href="mailto:support@scenariomock.com" className="text-primary underline underline-offset-4">
        support@scenariomock.com
      </a>
    ),
  },
  {
    label: '販売価格',
    value: `Pro プラン: 月額 ${PRO_MONTHLY_PRICE_USD} 米ドル(USD)。お支払いいただく金額は表示価格のみです(消費税は加算していません)。`,
  },
  {
    label: '商品代金以外の必要料金',
    value:
      'インターネット接続に必要な通信料金はお客様のご負担となります。米ドル建ての決済のため、カード会社所定の為替レートや海外事務手数料がかかる場合があります。',
  },
  {
    label: '契約期間・自動更新',
    value: `1か月単位の契約で、解約のお手続きをされるまで毎月自動で更新されます(契約期間の定めはありません)。目安として、1年間ご利用の場合のお支払い額は ${yearly} 米ドルです。`,
  },
  { label: '支払方法', value: 'クレジットカード(決済は Stripe を通じて行います)。' },
  {
    label: '支払時期',
    value: '初回はお申し込み時にお支払いいただきます。以降は毎月、初回のお申し込み日と同じ日に、翌1か月分が自動で請求されます。',
  },
  { label: 'サービスの提供時期', value: '決済の完了後、ただちに Pro プランの機能をご利用いただけます。' },
  {
    label: '解約(申込みの撤回・解除)',
    value: (
      <>
        <Link to="/billing" className="text-primary underline underline-offset-4">課金ページ</Link>
        の「プランを管理(解約・領収書)」から、いつでもオンラインで解約できます。解約後も、お支払い済みの期間の終了日まで Pro
        プランをご利用いただけ、次回以降の請求は行いません。お支払い済みの料金の日割りでの返金は行いません(法令に定める場合を除きます)。
      </>
    ),
  },
  { label: '申込みの有効期限', value: 'なし' },
  {
    label: '動作環境',
    value: '最新版の Google Chrome、Microsoft Edge、Mozilla Firefox、Safari(JavaScript を有効にしてください)。',
  },
]

export function CommercePage() {
  const { i18n } = useTranslation()
  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1">
        <div className="mx-auto max-w-3xl px-6 py-16 space-y-10">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">特定商取引法に基づく表記</h1>
            {i18n.language !== 'ja' && (
              <p className="text-sm text-muted-foreground">
                This page is the disclosure required by the Japanese Act on Specified Commercial Transactions and is provided in Japanese only.
              </p>
            )}
          </div>
          <Separator />
          <dl className="divide-y border-y">
            {ROWS.map((row) => (
              <div key={row.label} className="grid gap-1 py-4 sm:grid-cols-[12rem_1fr] sm:gap-6">
                <dt className="text-sm font-semibold">{row.label}</dt>
                <dd className="text-sm leading-relaxed text-muted-foreground">{row.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </main>
      <PublicFooter />
    </div>
  )
}
